/**
 * useVariantItemsLoader.js - Simple composable for loading variant items
 *
 * This composable provides a clean way to load items for a specific variant
 * from the Pinia ORM store. It's intentionally kept simple and focused on
 * data loading only, following SSOT principles.
 *
 * RESPONSIBILITIES:
 * - Load items for a variant from OrderBoxItem store
 * - Transform ORM items to editor-friendly format
 * - No state management (components manage their own state)
 *
 * USAGE:
 * const { loadVariantItems, calculateVariantSummary } = useVariantItemsLoader()
 * const items = loadVariantItems(boxId, productId, variantId)
 */

import { useRepo } from 'pinia-orm';
import { ItemStatus } from '@/enums/commerce/ItemStatus.js';
import OrderBoxItem from '@/models/commerce/OrderBoxItem.js';

export function useVariantItemsLoader() {
  /**
     * Load items for a specific variant from the store
     * Returns items in the format expected by VariantItemsEditor
     *
     * @param {number|string} boxId - OrderBox ID
     * @param {number|string} productId - Product ID
     * @param {number|string} variantId - Variant ID
     * @returns {Array} Items formatted for editor
     */
  const loadVariantItems = (boxId, productId, variantId) => {
    if (!boxId) return [];

    const boxIdInt = parseInt(boxId);
    const productIdInt = parseInt(productId);
    const variantIdInt = parseInt(variantId);

    const storedItems = useRepo(OrderBoxItem)
      .where('orderBoxId', boxIdInt)
      .where('variantId', variantIdInt)
      .where('productId', productIdInt)
      .get();

    // Transform ORM items to editor format
    return storedItems.map((item) => ({
      tempId: `existing-${item.id}`,
      existingId: item.id,
      qty: item.qty || 0,
      notes: item.notes || '',
      avatarUrl: item.avatarUrl || '',
      reservationStatus: item.reservationStatus || 'none',
      depositAmount: item.depositAmount || 0,
      reservedForName: item.reservedForName || '',
      status: item.status || ItemStatus.PENDING,
      // Same shape as CartPage's builder: the line's own «تدبيسة» flag.
      isResale: item.isResale ?? null,
    }));
  };

  /**
     * Calculate summary statistics for a variant
     * Useful for displaying totals in lists
     *
     * @param {number|string} boxId - OrderBox ID
     * @param {number|string} productId - Product ID
     * @param {number|string} variantId - Variant ID
     * @param {number} variantPrice - Price per unit (in cents)
     * @returns {Object} Summary with totals and preview images
     */
  const calculateVariantSummary = (boxId, productId, variantId, variantPrice) => {
    const items = loadVariantItems(boxId, productId, variantId);

    // Filter out cancelled items for calculations
    const activeItems = items.filter(item => item.status !== ItemStatus.CANCELLED);

    const totalQty = activeItems.reduce((sum, item) => sum + (item.qty || 0), 0);
    const totalAmount = activeItems.reduce((sum, item) => sum + ((item.qty || 0) * variantPrice), 0);
    const totalDeposit = activeItems.reduce((sum, item) => sum + (item.depositAmount || 0), 0);

    // Get up to 4 preview images
    const previewImages = activeItems
      .filter(item => item.avatarUrl)
      .slice(0, 4)
      .map(item => ({ avatarUrl: item.avatarUrl }));

    return {
      totalQty,
      itemsCount: activeItems.length,
      totalAmount,
      totalDeposit,
      previewImages,
    };
  };

  /**
     * Load items for all variants of a product
     * Returns a map of variantId -> items array
     *
     * @param {number|string} boxId - OrderBox ID
     * @param {number|string} productId - Product ID
     * @param {Array} variants - Array of variant objects with id property
     * @returns {Object} Map of variantId to items array
     */
  const loadAllVariantItems = (boxId, productId, variants) => {
    const itemsByVariant = {};

    variants.forEach((variant) => {
      itemsByVariant[variant.id] = loadVariantItems(boxId, productId, variant.id);
    });

    return itemsByVariant;
  };

  /**
     * Calculate summaries for all variants of a product
     * Returns a map of variantId -> summary object
     *
     * @param {number|string} boxId - OrderBox ID
     * @param {number|string} productId - Product ID
     * @param {Array} variants - Array of variant objects with id and price properties
     * @returns {Object} Map of variantId to summary object
     */
  const calculateAllVariantSummaries = (boxId, productId, variants) => {
    const summaries = {};

    variants.forEach((variant) => {
      summaries[variant.id] = calculateVariantSummary(boxId, productId, variant.id, variant.price);
    });

    return summaries;
  };

  return {
    loadVariantItems,
    calculateVariantSummary,
    loadAllVariantItems,
    calculateAllVariantSummaries,
  };
}
